/**
 * GC qualifying experience — the HITL proposal for the loan-root
 * `totalVerifiedGCExperience`.
 *
 * Owner decision 2026-09-22 (experience-workbook.md §"GC qualifying experience"):
 * the hired GC's verified track record is a SEPARATE number from guarantor
 * verified experience, and the loan's qualifying experience for borrower
 * classification is their SUM. The per-GC counts
 * (`generalContractors[].verifiedExperienceTotal`) are rolled up automatically on
 * every verify; the loan-root total is NEVER auto-written. The rollup enqueues a
 * proposal and underwriting applies / edits / rejects it.
 *
 *   list → GET  /admin/loans/{dealId}/opening-assistant        (writeback queue)
 *          filtered to fieldPath === "totalVerifiedGCExperience"
 *   act  → POST /admin/loans/{dealId}/opening-assistant/{queueId}/action
 *          { action: "apply" | "edit" | "reject", editedValue?, force? }
 *
 * Guideline gates: hired-GC experience counts on an EXCEPTION basis and needs an
 * executed Completion Guaranty and an active license. When any credited GC is
 * missing `providesCompletionGuaranty` / `licenseConfirmed`, the backend's
 * `reasoning` opens a sentence with `GUIDELINE GATES OPEN` — the row renders as a
 * warning and each gated GC links to its record so the toggles are one click
 * away. The proposal is still queued; the underwriter decides.
 *
 * The verify response carries only `{items, persistence}` — the proposal is
 * enqueued as a side effect of the rollup — so this card re-reads the queue when
 * the workbook says something was verified (`refreshKey`).
 *
 * Endpoint permissions are admin OR underwriter; for everyone else the card
 * renders the read-only state rather than calling and swallowing a 403.
 */
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import EngineeringOutlined from "@mui/icons-material/EngineeringOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  TextField,
  Tooltip,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import { losAiReviewsApi } from "../../../api/losAiReviewsApi";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { loanDrilldownSidebarTypes } from "../../../constants/enums/loanDrilldownScreenSidebarOptions";
import { usePermissionsHook } from "../../../hooks/usePermissionsHook";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import {
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../../redux/actions/sidebarActions";
import { detailSidebarClear } from "../../../redux/reducers/detailSidebarReducer";
import { FORM, formSx, px } from "../../../theme/formStyles";
import { fmtPoints } from "./experienceConstants";
import { slate, status } from "../../../theme/colors";

export const GC_EXPERIENCE_FIELD_PATH = "totalVerifiedGCExperience";

const gcName = (g) =>
  g?.company ||
  `${g?.firstName || ""} ${g?.lastName || ""}`.trim() ||
  "General contractor";

/**
 * The gates, recomputed from the loan so each one can name and LINK its GC.
 * The backend's reasoning is the authority on whether the proposal is gated
 * (it is rendered verbatim); this only decides which rows get a link.
 */
const openGates = (generalContractors) =>
  (generalContractors || [])
    .filter(Boolean)
    .filter((g) => Number(g?.verifiedExperienceTotal) > 0)
    .map((g) => {
      const missing = [];
      if (g?.providesCompletionGuaranty !== true)
        missing.push("completion guaranty");
      if (g?.licenseConfirmed !== true) missing.push("license confirmation");
      return missing.length ? { gc: g, missing } : null;
    })
    .filter(Boolean);

/**
 * Split the backend's `reasoning` into [arithmetic, gate sentence].
 *
 * The rollup appends "GUIDELINE GATES OPEN — …" to the end of an otherwise
 * descriptive paragraph. The gate is the only part that decides whether Apply
 * is safe, so it leads the card while the arithmetic folds away — rather than
 * both running four lines wide and neither being read.
 */
const splitReasoning = (reasoning) => {
  const text = String(reasoning || "").trim();
  if (!text) return ["", ""];
  const at = text.indexOf("GUIDELINE GATES OPEN");
  if (at < 0) return [text, ""];
  return [
    text.slice(0, at).trim(),
    text
      .slice(at)
      .replace(/^GUIDELINE GATES OPEN\s*[—-]?\s*/, "")
      .trim(),
  ];
};

const GcQualifyingExperienceCard = ({
  compact = false,
  refreshKey,
  borderColor,
  action,
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { getLoanById } = useUnderwritingHook();
  const { isAdmin, isUnderwriter } = usePermissionsHook();

  const loan = useSelector((s) => s.loanDrilldown);
  const dealId = loan?._id;
  const generalContractors = loan?.generalContractors;
  const appliedTotal = loan?.totalVerifiedGCExperience;

  const canAct = Boolean(isAdmin || isUnderwriter);

  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [showWhy, setShowWhy] = useState(false);
  // The panel layout folds the WHOLE reasoning body away; `showWhy` stays the
  // inner "How this was calculated" toggle, so the two never fight.
  const [showDetail, setShowDetail] = useState(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const fetchRow = useCallback(async () => {
    if (!dealId || !canAct) return;
    setLoading(true);
    try {
      const rows = await losAiReviewsApi.getOpeningAssistant(
        dealId,
        "pending_review",
      );
      const mine = (rows || []).filter(
        (r) => r?.fieldPath === GC_EXPERIENCE_FIELD_PATH,
      );
      // Newest wins — a re-proposal refreshes the pending row in place, but a
      // stale twin should never outrank the current ask.
      mine.sort((a, b) =>
        String(b?.ts || "").localeCompare(String(a?.ts || "")),
      );
      if (aliveRef.current) setRow(mine[0] || null);
    } catch {
      // 403 (not admin/underwriter) or an unreachable queue: the card falls
      // back to the read-only total rather than shouting.
      if (aliveRef.current) setRow(null);
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [dealId, canAct]);

  useEffect(() => {
    fetchRow();
  }, [fetchRow, refreshKey]);

  const perGc = useMemo(
    () =>
      (generalContractors || [])
        .filter(Boolean)
        .map((g) => ({
          id: String(g?._id),
          name: gcName(g),
          verified: Number(g?.verifiedExperienceTotal) || 0,
          gc: g,
        }))
        .sort((a, b) => b.verified - a.verified),
    [generalContractors],
  );

  const gates = useMemo(
    () => openGates(generalContractors),
    [generalContractors],
  );
  const gated =
    /GUIDELINE GATES OPEN/.test(row?.reasoning || "") || gates.length > 0;

  const openGcRecord = useCallback(
    (gc) => {
      dispatch(detailSidebarClear());
      dispatch(
        sidebarTypeSet(loanDrilldownSidebarTypes.ADD_LOAN_RELATED_CONTACT),
      );
      dispatch(sidebarSetStateValue({ ...gc, _gcRecord: true }));
    },
    [dispatch],
  );

  const refreshLoan = useCallback(() => {
    if (!dealId) return;
    getLoanById({
      loanId: dealId,
      // The user just asked for this write; apply the read even if a mutation
      // raced it (see utils/loan/loanWriteSeq.js).
      allowStale: true,
      onSuccessFn: (data) => dispatch(loanDrilldownSet(data)),
    });
  }, [dealId, getLoanById, dispatch]);

  const act = useCallback(
    async (payload, successMsg) => {
      const queueId = row?.queueId;
      if (!dealId || !queueId || busy) return;
      setBusy(true);
      try {
        await losAiReviewsApi.actionOpeningAssistant(dealId, queueId, payload);
        enqueueSnackbar(successMsg, { variant: "success" });
        setEditing(false);
        // Apply / edit write the loan through the provenance seam, so the
        // classification rollup only changes once the loan is re-read.
        if (payload.action !== "reject") refreshLoan();
        fetchRow();
      } catch (err) {
        const detail = err?.response?.data?.detail || "";
        if (
          (err?.response?.status === 409 || /manual|force/i.test(detail)) &&
          !payload.force &&
          payload.action !== "reject"
        ) {
          const ok = window.confirm(
            "GC verified experience was set manually on this loan. Overwrite it?",
          );
          if (ok) {
            setBusy(false);
            return act({ ...payload, force: true }, successMsg);
          }
        } else {
          enqueueSnackbar(detail || err?.message || "Action failed.", {
            variant: "error",
          });
        }
      } finally {
        setBusy(false);
      }
    },
    [row?.queueId, dealId, busy, enqueueSnackbar, refreshLoan, fetchRow],
  );

  const startEdit = () => {
    setEditValue(row?.proposedValue == null ? "" : String(row.proposedValue));
    setEditing(true);
  };

  const submitEdit = () => {
    const n = Number(editValue);
    if (!Number.isFinite(n) || n < 0) {
      enqueueSnackbar("Enter a number of verified items (0 or more).", {
        variant: "warning",
      });
      return;
    }
    act({ action: "edit", editedValue: n }, "GC verified experience updated.");
  };

  // Nothing proposed and nothing applied — there is no GC experience story to
  // tell on this loan yet. A host that hangs an `action` on the panel (the
  // Contacts tab's Experience Calculator link) keeps it, so the way to START
  // that story doesn't vanish with it.
  if (!row && appliedTotal == null && !loading && !(action && !compact))
    return null;

  // The gate is the only part of `reasoning` that decides whether Apply is
  // safe, so it leads; the arithmetic folds away.
  const [explain, gateText] = splitReasoning(row?.reasoning);

  // The panel body — the gate, the current→proposed pair, the per-GC list,
  // the edit field and the three actions. Shared by both layouts.
  //
  // `withActions: false` drops the Apply / Edit / Reject row, for the layout
  // that already carries those buttons on the proposal strip above — the body
  // is then purely the reasoning, and the buttons are never drawn twice.
  //
  // `flush: true` drops the side/top padding, for the panel layout, which has
  // no box around it for the padding to inset from.
  const renderDetailBody = ({ withActions = true, flush = false } = {}) => (
    <Box
      sx={
        flush
          ? { pt: px(FORM.s.s1), pb: 0 }
          : { px: px(FORM.s.s4), py: px(FORM.s.s3) }
      }
    >
      {!row ? (
        <Txt sx={{ ...formSx.helper, mt: 0, maxWidth: 560 }}>
          Applied as the loan&apos;s verified GC experience. A new proposal
          appears here when the experience workbook rolls up a different total.
        </Txt>
      ) : (
        <>
          {/* the gate — the one thing that decides whether Apply is safe */}
          {gated && (
            <Flx
              g={1}
              sx={{
                alignItems: "flex-start",
                mb: px(FORM.s.s3),
                px: px(FORM.s.s3),
                py: px(FORM.s.s2),
                borderRadius: px(FORM.radius.control),
                background: FORM.color.warnBg,
                border: `1px solid ${FORM.color.warnBorder}`,
              }}
            >
              <WarningAmberRounded
                sx={{ fontSize: 15, color: status.warning.fg, mt: "1px" }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Txt
                  sx={{
                    ...formSx.label,
                    mb: "2px",
                    fontWeight: 600,
                    color: status.warning.fg,
                  }}
                >
                  Guideline gates open
                </Txt>
                <Txt
                  sx={{
                    ...formSx.helper,
                    mt: 0,
                    color: status.warning.fg,
                    maxWidth: 620,
                  }}
                >
                  {gateText ||
                    "Hired-GC experience counts on an exception basis and needs an executed Completion Guaranty and an active license."}
                </Txt>
              </Box>
            </Flx>
          )}

          {/* current → proposed, as a figure pair rather than a sentence */}
          <Flx ac g={1.5} sx={{ mb: px(FORM.s.s3), flexWrap: "wrap" }}>
            <Figure
              label="On the loan"
              value={
                row.currentValue == null ? "Not set" : fmtPoints(row.currentValue)
              }
              muted
            />
            <ArrowForwardRounded
              sx={{ fontSize: 16, color: FORM.color.ink4 }}
            />
            <Figure label="Proposed" value={fmtPoints(row.proposedValue)} />
          </Flx>

          {/* what the number is made of — capped so it reads as a list, not
                a table stretched across the card */}
          {perGc.length > 0 && (
            <Box sx={{ maxWidth: 420, mb: px(FORM.s.s3) }}>
              {perGc.map((p) => {
                const gate = gates.find((g) => String(g.gc?._id) === p.id);
                return (
                  <Flx
                    key={p.id}
                    ac
                    jb
                    g={1}
                    sx={{
                      minHeight: 24,
                      minWidth: 0,
                      borderBottom: `1px solid ${FORM.color.divider}`,
                      "&:last-of-type": { borderBottom: "none" },
                    }}
                  >
                    <Flx ac g={0.5} sx={{ minWidth: 0 }}>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => openGcRecord(p.gc)}
                        sx={{
                          ...formSx.link,
                          fontSize: 12,
                          background: "none",
                          border: 0,
                          p: 0,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          textAlign: "left",
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {p.name}
                      </Box>
                      {gate && (
                        <Tooltip
                          title={`Missing ${gate.missing.join(
                            " and ",
                          )} — open the GC record to confirm`}
                        >
                          <WarningAmberRounded
                            sx={{ fontSize: 13, color: status.warning.fg }}
                          />
                        </Tooltip>
                      )}
                    </Flx>
                    <Txt
                      sx={{
                        ...formSx.meta,
                        flex: "none",
                        fontWeight: 600,
                        color:
                          p.verified > 0 ? FORM.color.ink : FORM.color.ink4,
                        fontVariantNumeric: "tabular-nums",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmtPoints(p.verified)} verified
                    </Txt>
                  </Flx>
                );
              })}
            </Box>
          )}

          {/* the backend's own words, folded away */}
          {explain && (
            <Box sx={{ mb: px(FORM.s.s3) }}>
              <Box
                component="button"
                type="button"
                onClick={() => setShowWhy((v) => !v)}
                aria-expanded={showWhy}
                sx={{
                  ...formSx.meta,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "2px",
                  background: "none",
                  border: 0,
                  p: 0,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  color: FORM.color.ink3,
                  "&:hover": { color: FORM.color.ink },
                }}
              >
                <KeyboardArrowDownRounded
                  sx={{
                    fontSize: 15,
                    transition: "transform .15s",
                    transform: showWhy ? "none" : "rotate(-90deg)",
                  }}
                />
                How this was calculated
              </Box>
              <Collapse in={showWhy} unmountOnExit>
                <Txt
                  sx={{
                    ...formSx.helper,
                    mt: px(FORM.s.s2),
                    maxWidth: 680,
                    color: FORM.color.ink2,
                  }}
                >
                  {explain}
                </Txt>
              </Collapse>
            </Box>
          )}

          {!canAct ? (
            <Txt sx={{ ...formSx.helper, mt: 0 }}>
              Underwriting or an admin applies this proposal.
            </Txt>
          ) : (
            <>
              <Collapse in={editing} unmountOnExit>
                <Flx ac g={1} sx={{ mb: px(FORM.s.s2), flexWrap: "wrap" }}>
                  <TextField
                    size="small"
                    type="number"
                    value={editValue}
                    autoFocus
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") submitEdit();
                      if (e.key === "Escape") setEditing(false);
                    }}
                    inputProps={{ min: 0, step: 0.5 }}
                    sx={{ width: 110, background: "#fff" }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    disabled={busy}
                    onClick={submitEdit}
                    sx={{ textTransform: "none" }}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    disabled={busy}
                    onClick={() => setEditing(false)}
                    sx={{ textTransform: "none" }}
                  >
                    Cancel
                  </Button>
                </Flx>
              </Collapse>

              {!editing && withActions && (
                <Flx ac g={0.75} sx={{ flexWrap: "wrap" }}>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    disabled={busy}
                    startIcon={
                      <CheckRounded sx={{ fontSize: "16px !important" }} />
                    }
                    onClick={() =>
                      act(
                        { action: "apply" },
                        "GC verified experience applied to the loan.",
                      )
                    }
                    sx={{
                      textTransform: "none",
                      minHeight: 30,
                      fontSize: 12.5,
                    }}
                  >
                    Apply
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="gray"
                    disabled={busy}
                    startIcon={
                      <EditOutlined sx={{ fontSize: "16px !important" }} />
                    }
                    onClick={startEdit}
                    sx={{
                      textTransform: "none",
                      minHeight: 30,
                      fontSize: 12.5,
                      background: "#fff",
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    disabled={busy}
                    onClick={() =>
                      act({ action: "reject" }, "Proposal rejected.")
                    }
                    sx={{
                      textTransform: "none",
                      minHeight: 30,
                      fontSize: 12.5,
                      color: slate[600],
                    }}
                  >
                    Reject
                  </Button>
                  {busy && <CircularProgress size={14} thickness={5} />}
                </Flx>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );

  // ── banner layout (`compact`) — one strip, for a tab's main column ────────
  // Same data and the same three actions as the panel; the detail body is the
  // panel's, revealed by "Details" rather than always open, so the strip costs
  // one row until an underwriter actually wants the arithmetic.
  if (compact) {
    const tone = row && gated ? "warning" : "info";
    const summary = row
      ? [
          perGc.length
            ? perGc
                .map((p) => `${p.name}: ${fmtPoints(p.verified)}`)
                .slice(0, 2)
                .join(", ") +
              (perGc.length > 2 ? ` +${perGc.length - 2} more` : "")
            : null,
          `Proposed on-loan total ${fmtPoints(row.proposedValue)}, currently ${
            row.currentValue == null ? "—" : fmtPoints(row.currentValue)
          }.`,
        ]
          .filter(Boolean)
          .join(". ")
      : `${fmtPoints(appliedTotal)} applied as the loan's verified GC experience.`;

    return (
      <Box
        sx={{
          border: `1px solid ${row ? status[tone].bg : FORM.color.divider}`,
          borderRadius: px(FORM.radius.control),
          background: row ? status[tone].bg : FORM.color.surface,
          overflow: "hidden",
        }}
      >
        <Flx
          ac
          jb
          g={1.5}
          sx={{ px: px(FORM.s.s3), py: px(FORM.s.s2), flexWrap: "wrap" }}
        >
          <Flx ac g={1} sx={{ minWidth: 0, flex: 1 }}>
            <Flx
              center
              sx={{
                width: 28,
                height: 28,
                borderRadius: px(FORM.radius.control),
                background: FORM.color.surface,
                border: `1px solid ${FORM.color.divider}`,
                flexShrink: 0,
              }}
            >
              <EngineeringOutlined
                sx={{ fontSize: 15, color: FORM.color.ink3 }}
              />
            </Flx>
            <Txt
              sx={{ ...formSx.label, mb: 0, fontWeight: 600, flexShrink: 0 }}
            >
              GC qualifying experience
            </Txt>
            <Txt sx={{ ...formSx.meta, minWidth: 0 }}>
              — {summary}{" "}
              {row ? (
                <Box
                  component="button"
                  type="button"
                  onClick={() => setShowWhy((v) => !v)}
                  sx={{
                    ...formSx.link,
                    fontSize: FORM.type.meta[0],
                    background: "none",
                    border: 0,
                    p: 0,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {showWhy ? "Hide" : "Details"}
                </Box>
              ) : null}
            </Txt>
            {loading && <CircularProgress size={12} thickness={5} />}
          </Flx>

          {row && canAct && !editing ? (
            <Flx ac g={0.5} sx={{ flexShrink: 0 }}>
              <Button
                size="small"
                variant="contained"
                disableElevation
                disabled={busy}
                onClick={() =>
                  act(
                    { action: "apply" },
                    "GC verified experience applied to the loan.",
                  )
                }
                sx={{ textTransform: "none", minHeight: 28, fontSize: 12.5 }}
              >
                Apply
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="gray"
                disabled={busy}
                onClick={startEdit}
                sx={{
                  textTransform: "none",
                  minHeight: 28,
                  fontSize: 12.5,
                  background: "#fff",
                }}
              >
                Edit
              </Button>
              <Button
                size="small"
                variant="text"
                disabled={busy}
                onClick={() => act({ action: "reject" }, "Proposal rejected.")}
                sx={{
                  textTransform: "none",
                  minHeight: 28,
                  fontSize: 12.5,
                  color: slate[600],
                }}
              >
                Reject
              </Button>
              {busy && <CircularProgress size={14} thickness={5} />}
            </Flx>
          ) : null}
        </Flx>

        {/* the panel's own body, folded away until asked for */}
        <Collapse in={!!row && (showWhy || editing)} unmountOnExit>
          <Box
            sx={{
              borderTop: `1px solid ${FORM.color.divider}`,
              background: FORM.color.surface,
            }}
          >
            {renderDetailBody()}
          </Box>
        </Collapse>
      </Box>
    );
  }

  // What "Keep" preserves — the number already on the loan, or nothing yet.
  const keepLabel =
    row?.currentValue == null ? "Keep as is" : `Keep ${fmtPoints(row.currentValue)}`;

  // Panel layout — a plain block at the top of the GC column, NOT a card: no
  // fill, no icon tile, no box. It reports one loan-level number, so it must not
  // read as another firm in the roster beneath it; a hairline under it is the
  // only separation. Colour is reserved for the gate (amber), which carries
  // meaning.
  return (
    <Box
      sx={{
        pb: px(FORM.s.s3),
        borderBottom: `1px solid ${borderColor || FORM.color.divider}`,
      }}
    >
      <Box>
        <Flx ac jb g={2} sx={{ flexWrap: "wrap" }}>
          <Box sx={{ minWidth: 0 }}>
            <Flx ac g={0.625} sx={{ minWidth: 0 }}>
              <Txt
                sx={{
                  fontSize: 13.5,
                  lineHeight: "20px",
                  fontWeight: 600,
                  color: FORM.color.ink,
                }}
              >
                GC qualifying experience
              </Txt>
              <Tooltip title="The hired contractor's OWN verified track record. Separate from guarantor experience — classification adds the two together.">
                <InfoOutlined
                  sx={{ fontSize: 14, color: FORM.color.ink4, cursor: "help" }}
                />
              </Tooltip>
              {loading && <CircularProgress size={12} thickness={5} />}
            </Flx>
            <Txt sx={{ ...formSx.helper, mt: "2px", maxWidth: "72ch" }}>
              Verified items credited to the contractors below.
            </Txt>
          </Box>

          <Flx ac g={1.5} sx={{ flexShrink: 0 }}>
            <Flx g={0.625} sx={{ alignItems: "baseline" }}>
              <Txt
                sx={{
                  fontSize: 16,
                  lineHeight: "22px",
                  fontWeight: 600,
                  color:
                    appliedTotal == null ? FORM.color.ink4 : FORM.color.ink,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {appliedTotal == null ? "—" : fmtPoints(appliedTotal)}
              </Txt>
              <Txt sx={{ ...formSx.meta, color: FORM.color.ink3 }}>
                {appliedTotal == null
                  ? "none applied"
                  : `project${Number(appliedTotal) === 1 ? "" : "s"} applied`}
              </Txt>
            </Flx>
            {action}
          </Flx>
        </Flx>

        {/* the proposal — one sentence and the decision, in that order */}
        {row && (
          <Flx
            ac
            jb
            g={1.5}
            sx={{
              mt: px(FORM.s.s3),
              px: px(FORM.s.s3),
              py: px(FORM.s.s2),
              flexWrap: "wrap",
              borderRadius: px(FORM.radius.control),
              background: gated ? FORM.color.warnBg : FORM.color.surface2,
              border: `1px solid ${
                gated ? FORM.color.warnBorder : FORM.color.divider
              }`,
            }}
          >
            <Flx ac g={0.75} sx={{ flex: 1, minWidth: 240 }}>
              {gated && (
                <WarningAmberRounded
                  sx={{ fontSize: 15, color: status.warning.fg, flexShrink: 0 }}
                />
              )}
              <Txt sx={{ ...formSx.body, color: FORM.color.ink }}>
                The workbook now shows{" "}
                <Box component="strong" sx={{ fontWeight: 700 }}>
                  {fmtPoints(row.proposedValue)} projects
                </Box>{" "}
                (currently{" "}
                {row.currentValue == null ? "none" : fmtPoints(row.currentValue)}).
              </Txt>
            </Flx>

            {canAct && !editing ? (
              <Flx ac g={0.5} sx={{ flexShrink: 0 }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="gray"
                  disabled={busy}
                  onClick={() => act({ action: "reject" }, "Proposal rejected.")}
                  sx={{
                    textTransform: "none",
                    minHeight: 30,
                    fontSize: 12.5,
                    background: "#fff",
                  }}
                >
                  {keepLabel}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  disabled={busy}
                  onClick={() =>
                    act(
                      { action: "apply" },
                      "GC verified experience applied to the loan.",
                    )
                  }
                  sx={{ textTransform: "none", minHeight: 30, fontSize: 12.5 }}
                >
                  Apply {fmtPoints(row.proposedValue)}
                </Button>
                {/* A third number is rare but the endpoint takes one, so the
                    way to it stays visible — quietly. */}
                <Button
                  size="small"
                  variant="text"
                  disabled={busy}
                  onClick={() => {
                    setShowDetail(true);
                    startEdit();
                  }}
                  sx={{
                    textTransform: "none",
                    minHeight: 30,
                    fontSize: 12.5,
                    color: slate[600],
                  }}
                >
                  Edit
                </Button>
                {busy && <CircularProgress size={14} thickness={5} />}
              </Flx>
            ) : !canAct ? (
              <Txt sx={{ ...formSx.meta, flexShrink: 0 }}>
                Underwriting or an admin applies this proposal.
              </Txt>
            ) : null}
          </Flx>
        )}

        {/* the arithmetic, the per-GC split and the gates — folded away */}
        {row && (
          <Box
            component="button"
            type="button"
            onClick={() => setShowDetail((v) => !v)}
            aria-expanded={showDetail}
            sx={{
              ...formSx.meta,
              mt: px(FORM.s.s2),
              display: "inline-flex",
              alignItems: "center",
              gap: "2px",
              background: "none",
              border: 0,
              p: 0,
              cursor: "pointer",
              fontFamily: "inherit",
              color: FORM.color.ink3,
              "&:hover": { color: FORM.color.ink },
            }}
          >
            <KeyboardArrowDownRounded
              sx={{
                fontSize: 15,
                transition: "transform .15s",
                transform: showDetail ? "none" : "rotate(-90deg)",
              }}
            />
            {showDetail ? "Hide detail" : "Show detail"}
          </Box>
        )}
      </Box>

      <Collapse in={Boolean(row) && (showDetail || editing)} unmountOnExit>
        <Box sx={{ mt: px(FORM.s.s2) }}>
          {renderDetailBody({ withActions: false, flush: true })}
        </Box>
      </Collapse>
    </Box>
  );
};

/** A labelled number in the current → proposed pair. */
const Figure = ({ label, value, muted = false }) => (
  <Box sx={{ minWidth: 0 }}>
    <Txt
      component="div"
      sx={{
        ...formSx.meta,
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: ".04em",
        textTransform: "uppercase",
        color: FORM.color.ink4,
        lineHeight: 1.4,
      }}
    >
      {label}
    </Txt>
    <Txt
      component="div"
      sx={{
        fontSize: muted ? 15 : 19,
        lineHeight: "24px",
        fontWeight: 600,
        color: muted ? FORM.color.ink3 : FORM.color.ink,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}
    </Txt>
  </Box>
);

Figure.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node,
  muted: PropTypes.bool,
};

GcQualifyingExperienceCard.propTypes = {
  /** Rail placement — reserved for density tweaks. */
  compact: PropTypes.bool,
  /** Bump to re-read the queue (e.g. after the workbook verifies an item). */
  refreshKey: PropTypes.any,
  /**
   * The panel layout's bottom hairline, for a host whose other boxes use a
   * different line colour (the Contacts tab darkens them a step).
   */
  borderColor: PropTypes.string,
  /**
   * Panel layout only: a control beside the applied figure (the Contacts tab's
   * Experience Calculator link). While set, the panel renders even with no
   * proposal and nothing applied.
   */
  action: PropTypes.node,
};

export default GcQualifyingExperienceCard;
